/**
 * Helpers for deriving display strings from a user's name.
 *
 * Single canonical name source post-BetterAuth migration: `user.name`
 * (BetterAuth-managed). The legacy `profiles.first_name` /
 * `profiles.last_name` split is being removed — these helpers replace the
 * ~30 inline `[firstName?.[0], lastName?.[0]].filter(Boolean).join('')`
 * patterns scattered across the codebase.
 *
 * Why a single full-name field instead of structured first/last:
 *   - Resilient across naming cultures (East Asian family-first, single
 *     names like "Madonna", names with multiple words / particles).
 *   - The user types what they want to be called; we don't try to parse
 *     "first" vs "last" from the structure of the name.
 *   - For greetings (toasts / chat / emails) we use the first whitespace-
 *     delimited word of the name. That works correctly for "Nicholas
 *     Tollemache" → "Nicholas" and degrades gracefully for "Madonna" →
 *     "Madonna".
 */
/** Trim + return null for empty. */
function effectiveName(user) {
    return user.name?.trim() || null;
}
/**
 * Full display name. Falls back to email local-part, then a generic
 * placeholder. Use this anywhere a list, header, or admin UI shows a
 * user's identity.
 */
export function getDisplayName(user) {
    const name = effectiveName(user);
    if (name)
        return name;
    const localPart = user.email?.split('@')[0]?.trim();
    if (localPart)
        return localPart;
    return 'User';
}
/**
 * "What should we call you" — first whitespace-delimited word of the name.
 * Use for chat greetings ("Hey Nicholas"), welcome-back toasts, and email
 * salutations. Falls back to email local-part, then a generic 'there'.
 */
export function getGreetingName(user) {
    const name = user.name?.trim();
    if (name) {
        const firstWord = name.split(/\s+/)[0];
        if (firstWord)
            return firstWord;
    }
    const localPart = user.email?.split('@')[0]?.trim();
    if (localPart)
        return localPart;
    return 'there';
}
/**
 * Up-to-2-letter initials for an avatar circle. Takes the first letter of
 * up to the first two whitespace-delimited words. "Nicholas Tollemache" →
 * "NT", "Madonna" → "M", "María José García López" → "MJ", empty/null →
 * first letter of email local-part, else "?".
 */
export function getInitials(user) {
    const name = effectiveName(user);
    if (name) {
        const words = name.split(/\s+/).filter(Boolean);
        if (words.length > 0) {
            return words
                .slice(0, 2)
                .map((w) => w[0])
                .join('')
                .toUpperCase();
        }
    }
    const localPart = user.email?.[0];
    return (localPart ?? '?').toUpperCase();
}
//# sourceMappingURL=userName.js.map